<script setup lang="ts">
    import { computed, useId } from 'vue'
    import { useI18n } from 'vue-i18n'
    import {
        EVALUATION_MODEL,
        TURN_EVALUATION_QUESTION_IDS,
        type UIChatMessage,
    } from 'models'

    const props = defineProps<{
        message: UIChatMessage
    }>()

    const { t: $t, te: $te, n: $n } = useI18n({ useScope: 'global' })
    const titleId = useId()

    /**
     * Where a boolean answer leans towards yes. Only a way of reading the
     * list: each consumer's threshold is chosen on the shadow records (spec
     * post-turn-evaluation, F2), and none is decided yet.
     */
    const LEANS_YES = 0.5

    /**
     * The evaluation of the turn after the reply (spec post-turn-evaluation):
     * shown after the steps, since it runs once the reply is written. On the
     * web it lands after the stream, so it appears when the conversation is
     * read again.
     */
    const record = computed(() => props.message.metadata?.turnEvaluation)

    /** Where a question sits in the list; one this build does not know goes last. */
    const orderOf = (id: string) => {
        const index = (
            TURN_EVALUATION_QUESTION_IDS as readonly string[]
        ).indexOf(id)
        return index === -1 ? TURN_EVALUATION_QUESTION_IDS.length : index
    }

    /**
     * One row per question, in the order they are asked: not the record's,
     * which jsonb sorts by key length. Only the answers with a probability: a
     * value of another shape (a later version asking a non boolean question)
     * would make `$n` throw and take the whole message detail down with it.
     */
    const questions = computed(() =>
        Object.entries(record.value?.probabilities ?? {})
            .filter(
                (entry): entry is [string, number] =>
                    typeof entry[1] === 'number' && Number.isFinite(entry[1]),
            )
            .sort(([a], [b]) => orderOf(a) - orderOf(b))
            .map(([id, probability]) => ({
                id,
                // A question of a later version this build does not know yet
                // keeps its id rather than disappearing.
                label: $te(`turnEvaluation.question.${id}`)
                    ? $t(`turnEvaluation.question.${id}`)
                    : id,
                probability,
            })),
    )

    /** The line of facts under the header, as in the tool selection. */
    const facts = computed(() => {
        if (!record.value) {
            return []
        }
        const { latencyMs, budgetMs, cost, inputTokens } = record.value
        return [
            {
                icon: 'ri:hourglass-line',
                label: $t('toolSelection.latency', {
                    budget: $n(budgetMs, 'integer'),
                }),
                value: `${$n(latencyMs, 'integer')} ms`,
            },
            {
                icon: 'ri:wallet-line',
                // Measured, never billed: in shadow it is a platform measure.
                label:
                    cost !== undefined
                        ? $t('turnEvaluation.cost', {
                              cost: $n(cost, {
                                  key: 'currencyUSD',
                                  maximumSignificantDigits: 3,
                              }),
                          })
                        : $t('toolSelection.notCharged'),
                value:
                    cost !== undefined
                        ? $n(cost, {
                              key: 'currencyUSD',
                              maximumSignificantDigits: 1,
                          })
                        : '-',
            },
            {
                icon: 'ri:ai-generate-2-line',
                label: $t('toolSelection.inputTokens'),
                value:
                    inputTokens !== undefined
                        ? `${$n(inputTokens, 'integer')} ${$t('label.tokens')}`
                        : '-',
            },
        ]
    })
</script>

<template>
    <section
        v-if="record"
        class="pk-turn-evaluation"
        :aria-labelledby="titleId">
        <header
            class="text-12 text-word-2 leading-none flex items-center gap-12 bg-surface-1 rounded-t border-t border-x border-surface-3 p-8">
            <div
                class="flex items-center bg-gray-lighten-1 text-white px-6 py-3 rounded">
                <VvIcon name="ri:scales-line" class="w-12 h-12" />
            </div>
            <strong :id="titleId" class="font-semibold text-12 truncate">
                {{ $t('turnEvaluation.title') }}
            </strong>
            <span
                v-if="record.failed"
                class="text-10 text-word-1 bg-surface-warning rounded px-4 py-2">
                {{ $t('toolSelection.failed') }}
            </span>
            <span
                v-else
                class="text-10 text-word-4 bg-surface-2 rounded px-4 py-2"
                :title="$t('turnEvaluation.modeHint')">
                {{ $t(`toolSelection.mode.${record.mode}`) }}
                <!-- The hover hint, for whoever cannot hover. -->
                <span class="u-visually-hidden">
                    {{ $t('turnEvaluation.modeHint') }}
                </span>
            </span>
            <code
                class="font-mono text-10 text-word-3 truncate ml-auto"
                :title="
                    $t('turnEvaluation.version', { version: record.version })
                ">
                {{ EVALUATION_MODEL }}
                <span class="u-visually-hidden">
                    {{
                        $t('turnEvaluation.version', {
                            version: record.version,
                        })
                    }}
                </span>
            </code>
        </header>
        <div class="border rounded-b border-surface-3">
            <p
                v-if="record.failed"
                class="p-8 border-b border-surface-3 text-12 text-word-2">
                {{
                    $t('turnEvaluation.leadFailed', {
                        budget: $n(record.budgetMs, 'integer'),
                    })
                }}
            </p>
            <dl
                class="flex flex-wrap items-center justify-between gap-x-12 gap-y-4 p-8 text-12 text-word-3"
                :class="{ 'border-b border-surface-3': questions.length > 0 }">
                <div
                    v-for="fact in facts"
                    :key="fact.icon"
                    class="flex items-center gap-6 min-w-0"
                    :title="fact.label">
                    <dt class="flex shrink-0">
                        <VvIcon :name="fact.icon" aria-hidden="true" />
                        <span class="u-visually-hidden">
                            {{ fact.label }}
                        </span>
                    </dt>
                    <dd class="tabular-nums truncate">
                        {{ fact.value }}
                    </dd>
                </div>
            </dl>
            <ul v-if="questions.length > 0">
                <li
                    v-for="question in questions"
                    :key="question.id"
                    class="pk-turn-evaluation__question"
                    :class="{
                        'pk-turn-evaluation__question--yes':
                            question.probability >= LEANS_YES,
                    }">
                    <span class="truncate" :title="question.label">
                        {{ question.label }}
                    </span>
                    <div
                        class="pk-turn-evaluation__probability"
                        :title="$t('turnEvaluation.probability')">
                        <!-- A picture of the value printed next to it. -->
                        <span
                            class="pk-turn-evaluation__bar"
                            aria-hidden="true"
                            :style="{
                                '--pk-turn-evaluation-probability': `${question.probability * 100}%`,
                            }" />
                        <span class="text-10 text-word-2 tabular-nums">
                            {{ $n(question.probability, 'decimal') }}
                        </span>
                    </div>
                </li>
            </ul>
        </div>
    </section>
</template>

<style lang="scss">
    .pk-turn-evaluation__question {
        display: grid;
        grid-template-columns: minmax(0, 1fr) 6rem;
        align-items: center;
        gap: var(--spacing-sm);
        min-height: var(--spacing-32);
        padding: var(--spacing-6) var(--spacing-8);
        font-size: var(--text-12);
        color: var(--color-word-3);

        & + & {
            border-top: var(--spacing-px) solid var(--color-surface-2);
        }
    }

    // An answer leaning towards yes, the ones the list is read for: weight
    // and a step of surface, no accent color (DESIGN.md).
    .pk-turn-evaluation__question--yes {
        background-color: var(--color-surface-1);
        color: var(--color-word-1);
        font-weight: var(--font-semibold);
    }

    .pk-turn-evaluation__probability {
        display: flex;
        align-items: center;
        gap: var(--spacing-6);
    }

    .pk-turn-evaluation__bar {
        position: relative;
        flex: 1;
        height: var(--spacing-4);
        overflow: hidden;
        border-radius: var(--rounded-full);
        background-color: var(--color-surface-2);

        &::after {
            content: '';
            position: absolute;
            inset: 0 auto 0 0;
            width: var(--pk-turn-evaluation-probability, 0%);
            border-radius: inherit;
            background-color: currentColor;
        }
    }
</style>
